import Constants from '@/utilities/constants'
import {
  dateBetween,
  doubleParentQuery, esClient, filterBuilder,
  parentCommunityQuery,
  parentCompanyQuery,
  parentQuery,
  parentQueryBuilder
} from '@/utilities/ptracSearch'

export async function taskSearch(esCreds, query, from = 0) {
  const searchBody = assembleElasticsearchQuery(query, Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS, from)

  if (Constants.ENVIRONMENTS.PRODUCTION !== process.env.VUE_APP_ENV) {
    console.log(JSON.stringify(searchBody, null, 2))
  }

  return await esClient(esCreds).search({
    index: 'ptrac',
    body: searchBody
  })
}

export function assembleElasticsearchQuery(stateQuery, maxResults, from = 0) {
  const searchBody = {
    from,
    size: maxResults,
    query: {
      bool: {
        must: [
          {match: {_class: Constants.ENTITY_FIELDS.TASK.CLASS}}
        ],
        should: [],
        filter: [],
        must_not: []
      }
    }
  }
  const jobFields = [
    'name',
    'number',
    'description',
    'community.name'
  ]

  const projectFields = [
    'name',
    'number',
    'address.blockNumber',
    'address.buildingNumber',
    'address.floorNumber',
    'address.city',
    'address.county',
    'address.jurisdiction',
    'address.lotNumber',
    'address.legalDescription',
    'address.legalSubdivision',
    'address.phase',
    'address.section',
    'address.state',
    'address.streetName',
    'address.streetNumber',
    'address.suiteNumber',
    'address.zip'
  ]

  const filterFields = [
    'name',
    'jobId',
    'priority',
    'ownerId'
  ]

  // General Search - fuzzy, wildcard, joined (job and parent)
  if (stateQuery.search) {
    const splitSearchTerms = stateQuery.search.toLowerCase().trim().split(" ")
    searchBody.query.bool.should.push(parentQuery(Constants.ENTITY_FIELDS.JOB.JOBS, jobFields, splitSearchTerms))
    searchBody.query.bool.should.push(doubleParentQuery(Constants.ENTITY_FIELDS.JOB.JOBS, Constants.ENTITY_FIELDS.PROJECT.PROJECTS, projectFields, splitSearchTerms))
    searchBody.query.bool['minimum_should_match'] = 1
  }

  // Company search
  if ((stateQuery.clientId || stateQuery.builderId) !== null) {
    searchBody.query.bool.must.push(parentCompanyQuery(Constants.ENTITY_FIELDS.JOB.JOBS, stateQuery))
  }
  // Community search
  if (stateQuery.communityId !== null) {
    searchBody.query.bool.must.push(parentCommunityQuery(Constants.ENTITY_FIELDS.JOB.JOBS, stateQuery))
  }
  // Statuses
  if (stateQuery.status.length > 0) {
    searchBody.query.bool.filter.push({match: {status: stateQuery.status.join(" ")}})
  }
  // Departments
  if (stateQuery.departmentId.length > 0) {
    searchBody.query.bool.filter.push(parentQueryBuilder(Constants.ENTITY_FIELDS.JOB.JOBS, "departmentId", stateQuery.departmentId.join(" ")))
  }
  // Assignees
  if (stateQuery.assigneeIds.length > 0) {
    searchBody.query.bool.filter.push({match: {assigneeId:  stateQuery.assigneeIds.join(" ")}})
  }
  // PO Number
  if (stateQuery.poNumber) {
    searchBody.query.bool.must.push({nested: {path: "fieldValues", query: {term: { "fieldValues.poNumber.keyword": stateQuery.poNumber }}}})
  }
  // Permit Number
  if (stateQuery.permitNumber) {
    searchBody.query.bool.filter.push(parentQueryBuilder(Constants.ENTITY_FIELDS.JOB.JOBS, "permitNumber", stateQuery.permitNumber))
  }

  // Filters
  searchBody.query.bool.filter.push(...filterBuilder(filterFields, stateQuery))

  // Date filters
  if (stateQuery.createdAt == null) {
    stateQuery.createdAt = []
  }
  if (stateQuery.createdAt.length > 0) {
    searchBody.query.bool.filter.push(dateBetween(stateQuery.createdAt, Constants.ENTITY_FIELDS.TASK.CREATED_AT))
  }
  if (stateQuery.dueDate == null) {
    stateQuery.dueDate = []
  }
  if (stateQuery.dueDate.length > 0) {
    searchBody.query.bool.filter.push(dateBetween(stateQuery.dueDate, Constants.ENTITY_FIELDS.TASK.DUE_DATE))
  }
  if (stateQuery.completionDate == null) {
    stateQuery.completionDate = []
  }
  if (stateQuery.completionDate.length > 0) {
    searchBody.query.bool.filter.push(dateBetween(stateQuery.completionDate, Constants.ENTITY_FIELDS.TASK.COMPLETION_DATE))
  }

  // Unassigned
  if (stateQuery.unassigned) {
    searchBody.query.bool.must_not.push({exists: {field: Constants.ENTITY_FIELDS.TASK.ASSIGNEE_ID}})
  }

  if (stateQuery.noPoNumber) {
    searchBody.query.bool.should.push(
      {
        bool: {
          must_not: [
            {
              nested: {
                path: "fieldValues",
                query: {
                  exists: {
                    field: "fieldValues.poNumber"
                  }
                }
              }
            }
          ]
        }
      },
      {
        nested: {
          path: "fieldValues",
          query: {
            bool: {
              must: [
                {
                  exists: {
                    field: "fieldValues.poNumber"
                  }
                },
                {
                  term: {
                    "fieldValues.poNumber.keyword": ""
                  }
                }
              ]
            }
          }
        }
      }
    )

    searchBody.query.bool.minimum_should_match = 1
  }

  if (stateQuery.reinspection) {
    searchBody.query.bool.must.push({
      nested:  {
        path: "fieldValues",
        query: {
          match: {
            "fieldValues.reinspection": "true"
          }
        }
      }
    })
  }

  if (stateQuery.poRequired) {
    searchBody.query.bool.must.push({
      nested:  {
        path: "fieldValues",
        query: {
          match: {
            "fieldValues.poRequired": "true"
          }
        }
      }
    })
  }

  // Exclude Invoice Tasks
  if (stateQuery.excludeInvoiceTasks) {
    searchBody.query.bool.must_not.push({
      match: {
        'name.keyword': Constants.TASKS.NAMES.INVOICE_SUBMITTAL
      }
    })
    searchBody.query.bool.must_not.push({
      match: {
        'name.keyword': Constants.TASKS.NAMES.INVOICE
      }
    })
  }

  return searchBody
}
